import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Star } from "lucide-react";
import useReveal from "../hooks/useReveal";
import useScrollY from "../hooks/useScrollY";
import CinematicSlide from "../components/CinematicSlide";
import WindPalm from "../components/WindPalm";
import PalmGrid from "../components/PalmGrid";
import SEO from "../components/SEO";
import {
  SITE,
  HERO_SLIDES,
  POOL_IMAGE,
  VILLA_EXTERIOR,
  POOL_TERRACE,
  SUITES,
  STATS,
  FEATURES,
  REVIEW,
  PALMS,
} from "../mock";

const whatsappLink = `https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
  "Hi Casa Elio! I'd like to book a stay in San Juan del Sur. Could you share availability and rates?"
)}`;

const Home = () => {
  const ref = useReveal();
  const scrollY = useScrollY();

  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Boutique Villa, San Juan del Sur"
        description="Casa Elio is a boutique villa above San Juan del Sur Bay in Nicaragua with 5 luxury suites — each with a king bed, ocean or mountain views, an infinity pool, and beautiful Pacific sunsets. Book direct on WhatsApp, Booking.com, or Airbnb."
        path="/"
      />
      {/* ============== CINEMATIC HERO ============== */}
      <CinematicSlide
        slides={HERO_SLIDES}
        eyebrow="San Juan del Sur · Nicaragua"
        headingLines={[
          { text: "Beautiful sunsets", italic: false },
          { text: "overlooking San Juan del Sur Bay!", italic: true },
        ]}
        body="Five luxury king suites where Persian sensibility meets the tropical coast — designed for stillness, light, and the long view of the ocean."
      />

      {/* ============== THE VILLA INTRO ============== */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
              The Villa
            </div>
            <h2 className="reveal font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1f2a24]">
              Five suites,
              <br /> one infinity pool,
              <br /> the open Pacific.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="reveal space-y-6 text-[#3a4a40] text-base md:text-[17px] leading-[1.85]">
              <p>
                Set on a quiet hillside minutes from San Juan del Sur Beach,
                Casa Elio is a private bed &amp; breakfast designed around water,
                breeze, and the slow horizon. Each suite opens to sea or
                mountain views; the salt-water infinity pool becomes the home's
                quiet center.
              </p>
              <p>
                Mornings begin with breakfast served in-room — fresh fruit,
                coffee, and choices that travel: American, vegetarian, vegan,
                Asian. Days end on the terrace, the Pacific gold and unhurried.
              </p>
            </div>
            <div className="reveal mt-10 flex flex-wrap gap-3">
              {FEATURES.map((f, i) => (
                <span
                  key={i}
                  className="px-4 py-2 border border-[#1f2a24]/20 text-[11px] uppercase tracking-[0.25em] text-[#1f2a24]"
                >
                  {f.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Twin parallax images */}
        <div className="mt-20 md:mt-32 grid md:grid-cols-12 gap-6 relative">
          <div className="md:col-span-7 overflow-hidden img-hover">
            <img
              src={POOL_IMAGE}
              alt="Infinity pool overlooking the Pacific"
              className="reveal-img w-full h-[88svh] md:h-[80vh] object-cover"
            />
          </div>
          <div
            className="md:col-span-5 md:mt-24 overflow-hidden img-hover"
            style={{ transform: `translate3d(0, ${scrollY * -0.04}px, 0)` }}
          >
            <img
              src={VILLA_EXTERIOR}
              alt="Open-air villa exterior"
              className="reveal-img w-full h-[70svh] md:h-[60vh] object-cover"
            />
          </div>
        </div>
      </section>

      {/* ============== STATS GRID ============== */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-3">
                In Numbers
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#1f2a24]">
                A small house. The big horizon.
              </h3>
            </div>
            <p className="max-w-md text-sm text-[#3a4a40] leading-relaxed">
              Boutique by design — only a handful of suites and one quiet
              terrace, so the villa never feels like a hotel.
            </p>
          </div>
          <div className="reveal">
            <PalmGrid items={STATS} />
          </div>
        </div>
      </section>

      {/* ============== SUITES PREVIEW ============== */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-4">
              Suites
            </div>
            <h2 className="reveal font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f2a24] leading-[1.05]">
              Five king suites,
              <br /> each with the sea.
            </h2>
          </div>
          <Link
            to="/rooms"
            className="reveal inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#1f2a24] link-underline"
          >
            Explore all suites <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {SUITES.map((s, i) => (
            <Link
              to="/rooms"
              key={s.id}
              className="group block"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="overflow-hidden img-hover bg-[#ebe3d2]">
                <img
                  src={s.image}
                  alt={s.name}
                  className="reveal-img w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="reveal mt-5 flex items-baseline justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-1">
                    {s.number} · {s.label}
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#1f2a24] group-hover:text-[#b6724a] transition-colors">
                    {s.name}
                  </h3>
                </div>
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#3a4a40] group-hover:text-[#1f2a24]">
                  King · {s.label}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============== EXPERIENCE TEASER ============== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={POOL_TERRACE}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ transform: `translate3d(0, ${(scrollY - 2400) * 0.08}px, 0) scale(1.08)` }}
          />
          <div className="absolute inset-0 bg-[#1f2a24]/55" />
        </div>

        {/* Strategic single silhouette palm */}
        <WindPalm
          src={PALMS.pair}
          side="left"
          offsetX={-130}
          bottom={-70}
          width={520}
          opacity={0.85}
          silhouette
          parallax={0.14}
          scrollIntensity={1.6}
          swayDuration={8.4}
          swayPhase={-1.5}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-44 text-[#f5f0e6] z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] opacity-80 mb-5">
            Experience
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Surf, salt water,
            <br /> and a slower hour.
          </h2>
          <p className="reveal mt-8 max-w-xl text-base md:text-lg leading-relaxed opacity-90">
            Less than a kilometer from the beach, Casa Elio sits at the edge of
            one of Central America's most loved surf coasts. Spend mornings on
            the water, afternoons in the pool, evenings watching the sun fold
            into the Pacific.
          </p>
          <Link
            to="/experience"
            className="reveal mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] border-b border-[#f5f0e6]/60 pb-2 hover:border-[#f5f0e6] transition-colors"
          >
            Discover the experience <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ============== REVIEW ============== */}
      <section className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
        <div className="reveal flex items-center justify-center gap-2 text-[#b6724a] mb-6">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
          ))}
          <span className="ml-3 text-[11px] uppercase tracking-[0.3em] text-[#3a4a40]">
            Guest reviews · {REVIEW.rating}
          </span>
        </div>
        <blockquote className="reveal font-serif-display italic text-2xl md:text-4xl leading-[1.3] text-[#1f2a24]">
          &ldquo;{REVIEW.quote}&rdquo;
        </blockquote>
        <div className="reveal mt-8 text-[11px] uppercase tracking-[0.3em] text-[#3a4a40]">
          {REVIEW.author} · {REVIEW.source}
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="relative bg-[#ebe3d2] overflow-hidden">
        {/* One strategic palm at bottom-right of the CTA */}
        <WindPalm
          src={PALMS.pair}
          side="right"
          offsetX={-120}
          bottom={-50}
          width={420}
          opacity={0.55}
          parallax={0.08}
          scrollIntensity={1.2}
          swayDuration={9.0}
          swayPhase={-3}
          hideOnMobile
        />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-5">
            Stay With Us
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-6xl text-[#1f2a24] leading-[1.05]">
            Step into the slower coast.
          </h2>
          <p className="reveal mt-6 max-w-xl mx-auto text-[#3a4a40] text-base md:text-lg leading-relaxed">
            Reach out directly on WhatsApp, or reserve through Booking.com.
          </p>
          <div className="reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={SITE.airbnbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#1f2a24]/30 text-[#1f2a24] hover:bg-[#1f2a24] hover:text-[#f5f0e6] transition-colors"
            >
              Airbnb
              <ArrowRight size={14} />
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.3em] px-7 py-4 bg-[#2f4438] text-[#f5f0e6] hover:bg-[#1f2a24] transition-colors"
            >
              <MessageCircle size={14} />
              Book Now
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#1f2a24]/30 text-[#1f2a24] hover:bg-[#1f2a24] hover:text-[#f5f0e6] transition-colors"
            >
              Booking.com
              <ArrowRight size={14} />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#1f2a24] link-underline"
            >
              Or send us a message <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
